import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ChatMessage } from '../../grading.types';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import { GradingBadgeService } from '../../services/grading-badge.service';

@Component({
  selector: 'app-chat-messages-list',
  standalone: true,
  imports: [CommonModule, ChatMessageComponent],
  templateUrl: './chat-messages-list.component.html',
  styleUrl: './chat-messages-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessagesListComponent implements AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly badgeService = inject(GradingBadgeService);

  readonly messages = input.required<ChatMessage[]>();
  readonly isSending = input<boolean>(false);

  protected readonly messagesContainerRef = viewChild<ElementRef<HTMLDivElement>>('messagesContainer');

  private isInitialLoad = true;
  private contentLoadedScrollTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Subscribe to messages changes to trigger scroll
    toObservable(this.messages)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        setTimeout(() => {
          this.scrollToBottom(this.isInitialLoad ? 'instant' : 'smooth');
          this.isInitialLoad = false;
        });
      });

    // Scroll when AI starts responding (to show typing indicator)
    toObservable(this.isSending)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isSending) => {
        if (isSending) {
          setTimeout(() => this.scrollToBottom('smooth'));
        }
      });

    // Subscribe to scroll to grading requests from the badge
    this.badgeService.scrollToGrading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.scrollToLastGradingResults();
      });
  }

  ngAfterViewInit(): void {
    // Initial scroll on view init
    setTimeout(() => this.scrollToBottom('instant'));
  }

  /**
   * Called when a chat message's async content (tool previews) finishes loading.
   * Scrolls to bottom to ensure newly loaded content is visible.
   * Debounced to avoid rapid consecutive scrolls when multiple contents load.
   */
  protected onContentLoaded(): void {
    // Debounce: clear any pending scroll and schedule a new one
    if (this.contentLoadedScrollTimeout) {
      clearTimeout(this.contentLoadedScrollTimeout);
    }
    this.contentLoadedScrollTimeout = setTimeout(() => {
      this.scrollToBottom('smooth');
      this.contentLoadedScrollTimeout = null;
    }, 50);
  }

  private scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
    const container = this.messagesContainerRef()?.nativeElement;
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior,
      });
    }
  }

  /**
   * Scrolls to the last grading results preview in the chat
   */
  private scrollToLastGradingResults(): void {
    const container = this.messagesContainerRef()?.nativeElement;
    if (!container) return;

    // Find all grading results preview elements
    const gradingElements = container.querySelectorAll('app-grading-results-preview');
    if (gradingElements.length === 0) return;

    // Get the last one
    const lastGradingElement = gradingElements[gradingElements.length - 1];

    // Scroll to it
    lastGradingElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }
}
